<template>
  <div>
    <hr-dropdown-menu
      :label="$t('library.listItemBulkEdit')"
      color="secondary"
      size="sm"
    >
      <hr-list>
        <hr-list-item
          v-close-popup
          clickable
          :label="$t('programs.buttonBulkEditTreatmentAreas')"
          @click="onEditTreatmentAreaClick"
        />
        <hr-list-item
          v-close-popup
          clickable
          :label="$t('programs.buttonBulkAddProgramTags')"
          @click="onAddTagClick"
        />
        <hr-list-item
          v-close-popup
          clickable
          :label="$t('programs.buttonBulkRemoveProgramTags')"
          @click="onRemoveAddTagClick"
        />
        <hr-list-item
          v-close-popup
          clickable
          :label="$t('programs.buttonBulkAddProgramFields')"
          @click="onAddProgramFields"
        />
        <hr-list-item
          v-close-popup
          clickable
          :label="$t('programs.buttonBulkRemoveProgramFields')"
          @click="onRemoveProgramFields"
        />
      </hr-list>
    </hr-dropdown-menu>
  </div>
</template>

<script setup>
import { i18n } from 'boot/i18n';
import BulkManageDataFieldsDialog from 'components/Programs/BulkManageDataFieldsDialog.vue';
import TagSelectDialog from 'components/Tags/TagSelectDialog.vue';
import TreatmentAreaSelectDialog from 'components/TreatmentPlans/TreatmentAreaSelectDialog.vue';
import { useQuasar } from 'quasar';
import { useComponentDialog } from 'src/composables/componentDialog';
import { notifyNegative, notifySuccess } from 'src/composables/interactions';
import { HrDropdownMenu, HrList, HrListItem } from 'src/design-system';
import ProgramsDB from 'src/fb/programs-db';
import { programs as programsStore } from 'src/store/programs';
import { logEvent } from 'src/utils/LogEvent';

const { syncLibraryPrograms } = programsStore();

const { openComponentDialog } = useComponentDialog();
const $q = useQuasar();

const props = defineProps({
  selectedPrograms: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['update:programs']);
const programsDb = new ProgramsDB();

const syncToSearchEngine = async ({ programs, changeType }) => {
  try {
    await syncLibraryPrograms({ programs, changeType });
  } catch (err) {
    // Even if a request fails, it shouldn't stop code execution since synchronization runs daily.
    console.error('Error synchronizing with search engine:', err);
    logEvent(err);
  }
}; // syncToSearchEngine()

const bulkEditTreatmentArea = async (selectedTreatmentArea, selectedSubTreatmentArea) => {
  const programTreatmentAreaUpdates = [];
  $q.loading.show();

  try {
    props.selectedPrograms.forEach((program) => {
      programTreatmentAreaUpdates.push(programsDb.set(
        {
          treatmentArea: selectedTreatmentArea,
          subTreatmentArea: selectedSubTreatmentArea || null,
        },
        program.id,
      ));
    });

    const results = await Promise.allSettled(programTreatmentAreaUpdates);
    const failures = results.filter((result) => result.status === 'rejected');
    if (failures.length > 0) {
      console.error('Some updates failed:', failures);
      notifyNegative(i18n.t('programs.notifyBulkEditProgramsTreatmentAreasFailure'));
      return;
    }

    // Update programs locally
    const updatedPrograms = props.selectedPrograms.map((program) => ({
      ...program,
      treatmentArea: selectedTreatmentArea,
      subTreatmentArea: selectedSubTreatmentArea || null,
    }));

    await syncToSearchEngine({ programs: updatedPrograms, changeType: 'UPDATE' });
    emit('update:programs', updatedPrograms);

    notifySuccess(i18n.t('programs.notifyBulkEditProgramsTreatmentAreasSuccess'));
  } catch (error) {
    console.error('Error updating treatment areas:', error);
    notifyNegative(i18n.t('programs.notifyBulkEditProgramsTreatmentAreasFailure'));
  } finally {
    $q.loading.hide();
  }
}; // bulkEditTreatmentArea()

const bulkAddTags = async (selectedTags) => {
  $q.loading.show();

  const programsWithTags = [];
  const updatedPrograms = [];

  try {
    const programStatusActions = props.selectedPrograms.map(async (program) => {
      const programTags = Array.from(new Set([
        ...(program.tags || []),
        ...selectedTags,
      ]));

      const updatedProgram = {
        ...program,
        tags: programTags,
      };

      programsWithTags.push(updatedProgram);
      updatedPrograms.push(updatedProgram);
      return programsDb.set({ tags: programTags }, program.id);
    });

    await Promise.all(programStatusActions);
    await syncToSearchEngine({ programs: programsWithTags, changeType: 'UPDATE' });

    emit('update:programs', updatedPrograms);
    notifySuccess(i18n.t('programs.notifyBulkAddProgramsTagsSuccess'));
  } catch (error) {
    console.error('Error adding tags to programs:', error);
    notifyNegative(i18n.t('programs.notifyBulkAddProgramsTagsFailure'));
  } finally {
    $q.loading.hide();
  }
}; // bulkAddTags()

const bulkRemoveTags = async (selectedTags) => {
  $q.loading.show();

  const programsWithTags = [];
  const updatedPrograms = [];

  try {
    const programStatusActions = props.selectedPrograms.map(async (program) => {
      const programTags = (program.tags || []).filter((tag) => !selectedTags.includes(tag));

      const updatedProgram = {
        ...program,
        tags: programTags,
      };

      programsWithTags.push(updatedProgram);
      updatedPrograms.push(updatedProgram);
      return programsDb.set({ tags: programTags }, program.id);
    });

    await Promise.all(programStatusActions);
    await syncToSearchEngine({ programs: programsWithTags, changeType: 'UPDATE' });

    emit('update:programs', updatedPrograms);
    notifySuccess(i18n.t('programs.notifyBulkRemoveProgramsTagsSuccess'));
  } catch (error) {
    console.error('Error removing tags from programs:', error);
    notifyNegative(i18n.t('programs.notifyBulkRemoveProgramsTagsFailure'));
  } finally {
    $q.loading.hide();
  }
}; // bulkRemoveTags()

const bulkAddProgramFields = async (selectedProgramFields) => {
  $q.loading.show();

  const updatedPrograms = [];

  const defaultFields = selectedProgramFields.filter((f) => !f.hideByDefault).map((f) => f.id);
  const hideByDefaultFields = selectedProgramFields.filter((f) => f.hideByDefault).map((f) => f.id);

  try {
    const programFieldUpdateActions = props.selectedPrograms.map((program) => {
      // for default fields: remove from hiddenCustomData (to show them)
      const updatedHiddenCustomData = [...(program.hiddenCustomData ?? [])].filter((item) => {
        return !defaultFields.includes(item);
      });

      // for hideByDefault fields: add to shownCustomData (to show them)
      const updatedShownCustomData = Array.from(new Set([
        ...(program.shownCustomData ?? []),
        ...hideByDefaultFields,
      ]));

      updatedPrograms.push({
        ...program,
        hiddenCustomData: updatedHiddenCustomData,
        shownCustomData: updatedShownCustomData,
      });

      return programsDb.set(
        { hiddenCustomData: updatedHiddenCustomData, shownCustomData: updatedShownCustomData },
        program.id,
      );
    });

    await Promise.all(programFieldUpdateActions);
    await syncToSearchEngine({ programs: updatedPrograms, changeType: 'UPDATE' });

    emit('update:programs', updatedPrograms);
    notifySuccess(i18n.t('programs.notifyBulkAddProgramFieldsSuccess'));
  } catch (error) {
    console.error('Error adding fields to programs:', error);
    notifyNegative(i18n.t('programs.notifyBulkAddProgramFieldsFailure'));
  } finally {
    $q.loading.hide();
  }
}; // bulkAddProgramFields()

const bulkRemoveProgramFields = async (selectedProgramFields) => {
  $q.loading.show();

  const updatedPrograms = [];

  const defaultFields = selectedProgramFields.filter((f) => !f.hideByDefault).map((f) => f.id);
  const hideByDefaultFields = selectedProgramFields.filter((f) => f.hideByDefault).map((f) => f.id);

  try {
    const programFieldUpdateActions = props.selectedPrograms.map((program) => {
      // for default fields: add to hiddenCustomData (to hide them)
      const updatedHiddenCustomData = Array.from(new Set([
        ...(program.hiddenCustomData ?? []),
        ...defaultFields,
      ]));

      // for hideByDefault fields: remove from shownCustomData (to hide them)
      const updatedShownCustomData = [...(program.shownCustomData ?? [])].filter((item) => {
        return !hideByDefaultFields.includes(item);
      });

      updatedPrograms.push({
        ...program,
        hiddenCustomData: updatedHiddenCustomData,
        shownCustomData: updatedShownCustomData,
      });

      return programsDb.set(
        { hiddenCustomData: updatedHiddenCustomData, shownCustomData: updatedShownCustomData },
        program.id,
      );
    });

    await Promise.all(programFieldUpdateActions);
    await syncToSearchEngine({ programs: updatedPrograms, changeType: 'UPDATE' });

    emit('update:programs', updatedPrograms);
    notifySuccess(i18n.t('programs.notifyBulkRemoveProgramFieldsSuccess'));
  } catch (error) {
    console.error('Error removing fields from programs:', error);
    notifyNegative(i18n.t('programs.notifyBulkRemoveProgramFieldsFailure'));
  } finally {
    $q.loading.hide();
  }
}; // bulkRemoveProgramFields()

const onEditTreatmentAreaClick = () => {
  openComponentDialog(
    TreatmentAreaSelectDialog,
    {
      title: i18n.t('programs.buttonBulkEditTreatmentAreas'),
      subTitle: i18n.t('programs.infoBulkEditTreatmentAreas'),
      submitButtonLabel: i18n.t('common.buttonSave'),
    },
    async ({ selectedTreatmentArea, selectedSubTreatmentArea }) => {
      await bulkEditTreatmentArea(selectedTreatmentArea, selectedSubTreatmentArea);
    },
  );
}; // onEditTreatmentAreaClick()

const onAddTagClick = () => {
  openComponentDialog(
    TagSelectDialog,
    {
      title: i18n.t('programs.buttonBulkAddProgramTags'),
      subTitle: i18n.t('programs.infoBulkAddProgramTags'),
      submitButtonLabel: i18n.t('common.buttonSave'),
      isRemoving: false,
    },
    async ({ selectedTags }) => {
      await bulkAddTags(selectedTags);
    },
  );
}; // onAddTagClick()

const onRemoveAddTagClick = () => {
  openComponentDialog(
    TagSelectDialog,
    {
      title: i18n.t('programs.buttonBulkRemoveProgramTags'),
      subTitle: i18n.t('programs.infoBulkRemoveProgramTags'),
      submitButtonLabel: i18n.t('common.buttonSave'),
      isRemoving: true,
      currentTags: props.selectedPrograms.flatMap((program) => program.tags || []),
    },
    async ({ selectedTags }) => {
      await bulkRemoveTags(selectedTags);
    },
  );
}; // onRemoveAddTagClick()

const onAddProgramFields = () => {
  openComponentDialog(
    BulkManageDataFieldsDialog,
    {
      title: i18n.t('programs.buttonBulkAddProgramFields'),
      subTitle: i18n.t('programs.infoBulkAddProgramFields'),
      submitButtonLabel: i18n.t('common.buttonSave'),
      isRemoveMode: false,
    },
    async (selectedFields) => {
      await bulkAddProgramFields(selectedFields);
    },
  );
}; // onAddProgramFields()

const onRemoveProgramFields = () => {
  openComponentDialog(
    BulkManageDataFieldsDialog,
    {
      title: i18n.t('programs.buttonBulkRemoveProgramFields'),
      subTitle: i18n.t('programs.infoBulkRemoveProgramFields'),
      submitButtonLabel: i18n.t('common.buttonSave'),
      isRemoveMode: true,
    },
    async (selectedFields) => {
      await bulkRemoveProgramFields(selectedFields);
    },
  );
}; // onRemoveProgramFields()

</script>
